import * as THREE from 'three';
import type { Renderer } from './Renderer.js';
import { PASSENGER_FIRE_COOLDOWN_MS } from '@lucky-vr/common';
import type { DriverInput, PassengerInput, Vec3 } from '@lucky-vr/common';

export class InputManager {
  // Keyboard/mouse state
  private keys = new Set<string>();
  private mouseX: number = 0;
  private mouseY: number = 0;
  private mouseDown: boolean = false;

  // Smooth steering (NFS-style analog feel from digital keys)
  private steeringValue: number = 0;
  private readonly steeringSpeed = 6.0;    // fast response when holding key
  private readonly steeringReturn = 8.0;   // snaps back quickly when released
  private readonly steeringMax = 1.0;
  private vrSteeringValue = 0;
  private wristNeutralRoll: number | null = null;
  private readonly vrSteeringDeadzone = 0.12;
  private readonly vrSteeringResponse = 13;

  // XR controller state, keyed by real handedness (index 0/1 is NOT
  // guaranteed left/right — the 'connected' event's XRInputSource is)
  private xrHands: {
    left?: { controller: THREE.XRTargetRaySpace; gamepad?: Gamepad; trigger: boolean; squeeze: boolean };
    right?: { controller: THREE.XRTargetRaySpace; gamepad?: Gamepad; trigger: boolean; squeeze: boolean };
  } = {};
  private renderer: Renderer;
  private isVR: boolean = false;

  private raycaster = new THREE.Raycaster();
  private mouseNDC = new THREE.Vector2();

  // desktop dual-wield: one mouse button, guns alternate
  private dualToggle = false;
  private lastDualToggle = 0;

  constructor(renderer: Renderer) {
    this.renderer = renderer;

    // Keyboard
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      // Prevent scrolling with arrow keys / space
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Space'].includes(e.code)) {
        e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));

    // Mouse
    window.addEventListener('mousemove', (e) => {
      this.mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      this.mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
    });
    window.addEventListener('mousedown', () => (this.mouseDown = true));
    window.addEventListener('mouseup', () => (this.mouseDown = false));
  }

  setupXRControllers(): void {
    const xr = this.renderer.renderer.xr;

    for (let i = 0; i < 2; i++) {
      const controller = xr.getController(i);
      if (controller.userData.inputBound) continue; // survive VR re-entry
      controller.userData.inputBound = true;

      // handedness + gamepad arrive via the XRInputSource on 'connected'
      controller.addEventListener('connected', (event: any) => {
        const handedness = event.data?.handedness;
        if (handedness !== 'left' && handedness !== 'right') return;
        const hand = handedness as 'left' | 'right';
        controller.userData.hand = hand;
        controller.userData.gamepad = event.data?.gamepad;
        // the Gamepad is a live object — axes/buttons update in place
        this.xrHands[hand] = { controller, gamepad: event.data?.gamepad, trigger: false, squeeze: false };
        if (hand === 'left') this.wristNeutralRoll = null;
      });
      controller.addEventListener('disconnected', () => {
        const hand = controller.userData.hand as 'left' | 'right' | undefined;
        if (hand && this.xrHands[hand]?.controller === controller) {
          delete this.xrHands[hand];
        }
      });

      // trigger + grip via the WebXR primary/squeeze actions — these fire on
      // every runtime, unlike Gamepad button polling
      const hand = () => {
        const h = controller.userData.hand as 'left' | 'right' | undefined;
        return h ? this.xrHands[h] : undefined;
      };
      controller.addEventListener('selectstart', () => { const h = hand(); if (h) h.trigger = true; });
      controller.addEventListener('selectend', () => { const h = hand(); if (h) h.trigger = false; });
      controller.addEventListener('squeezestart', () => { const h = hand(); if (h) h.squeeze = true; });
      controller.addEventListener('squeezeend', () => { const h = hand(); if (h) h.squeeze = false; });
    }

    this.isVR = true;
  }

  endXRSession(): void {
    this.isVR = false;
    this.xrHands = {};
    this.vrSteeringValue = 0;
    this.wristNeutralRoll = null;
  }

  update(dt: number, _xrFrame?: XRFrame): void {
    // Smooth steering from keyboard input
    const safeDt = Math.min(0.05, Math.max(0, dt));
    const left = this.keys.has('KeyA') || this.keys.has('ArrowLeft');
    const right = this.keys.has('KeyD') || this.keys.has('ArrowRight');

    if (left && !right) {
      this.steeringValue = Math.max(-this.steeringMax,
        this.steeringValue - this.steeringSpeed * safeDt);
    } else if (right && !left) {
      this.steeringValue = Math.min(this.steeringMax,
        this.steeringValue + this.steeringSpeed * safeDt);
    } else {
      const returnStep = this.steeringReturn * safeDt;
      this.steeringValue = Math.abs(this.steeringValue) <= returnStep
        ? 0
        : this.steeringValue - Math.sign(this.steeringValue) * returnStep;
    }

    if (this.isVR) {
      const target = this.shapeVRSteering(this.readRawVRSteering());
      const alpha = 1 - Math.exp(-this.vrSteeringResponse * safeDt);
      this.vrSteeringValue += (target - this.vrSteeringValue) * alpha;
      if (Math.abs(this.vrSteeringValue) < 0.002 && target === 0) this.vrSteeringValue = 0;
    }
  }

  get currentSteering(): number {
    // in VR the visual layer (body roll, steering wheel) must follow the
    // stick too, not just the keyboard value
    return this.isVR ? this.vrSteeringValue : this.steeringValue;
  }

  /** Left thumbstick X (xr-standard), touchpad fallback, then calibrated wrist roll. */
  private readRawVRSteering(): number {
    const left = this.xrHands.left;
    if (left?.gamepad) {
      const axes = left.gamepad.axes;
      const stickX = Number.isFinite(axes[2]) ? axes[2] : (axes[0] ?? 0);
      return THREE.MathUtils.clamp(stickX, -1, 1);
    }
    if (left) {
      const euler = new THREE.Euler().setFromQuaternion(left.controller.quaternion);
      if (this.wristNeutralRoll === null) this.wristNeutralRoll = euler.z;
      const delta = Math.atan2(
        Math.sin(euler.z - this.wristNeutralRoll),
        Math.cos(euler.z - this.wristNeutralRoll),
      );
      return THREE.MathUtils.clamp(delta / (Math.PI / 5), -1, 1);
    }
    return 0;
  }

  /** Remove hardware drift, then keep fine control near centre without losing full lock. */
  private shapeVRSteering(raw: number): number {
    const magnitude = Math.abs(raw);
    if (magnitude <= this.vrSteeringDeadzone) return 0;
    const normalized = (magnitude - this.vrSteeringDeadzone) / (1 - this.vrSteeringDeadzone);
    const curved = normalized * 0.72 + normalized ** 3 * 0.28;
    return Math.sign(raw) * curved;
  }

  getDriverInput(): DriverInput {
    if (this.isVR) {
      return this.getVRDriverInput();
    }
    return this.getKeyboardDriverInput();
  }

  getPassengerInput(): PassengerInput {
    if (this.isVR) {
      return this.getVRPassengerInput();
    }
    return this.getMousePassengerInput();
  }

  /** World-space aim direction: a ray from the camera through the cursor. */
  getWorldAimDir(): Vec3 {
    this.mouseNDC.set(this.mouseX, this.mouseY);
    this.raycaster.setFromCamera(this.mouseNDC, this.renderer.camera);
    const d = this.raycaster.ray.direction;
    return { x: d.x, y: d.y, z: d.z };
  }

  private getKeyboardDriverInput(): DriverInput {
    return {
      lateral: this.steeringValue,
      boost: this.keys.has('ShiftLeft') || this.keys.has('ArrowUp'),
      brake: this.keys.has('Space') || this.keys.has('ArrowDown'),
      aimDir: this.getWorldAimDir(),
      shoot: this.mouseDown,
    };
  }

  private getMousePassengerInput(): PassengerInput {
    // Alternate the two guns while the button is held — each gun has its own
    // server cooldown, so this interleaves into a fast left/right rhythm.
    const now = performance.now();
    if (now - this.lastDualToggle >= PASSENGER_FIRE_COOLDOWN_MS / 2) {
      this.dualToggle = !this.dualToggle;
      this.lastDualToggle = now;
    }

    const aim = this.getWorldAimDir();
    return {
      aimDirL: aim,
      shootL: this.mouseDown && this.dualToggle,
      aimDirR: aim,
      shootR: this.mouseDown && !this.dualToggle,
      reload: this.keys.has('KeyR'),
      special: this.keys.has('KeyQ'),
    };
  }

  private getVRDriverInput(): DriverInput {
    const left = this.xrHands.left;
    const right = this.xrHands.right;

    const lateral = this.vrSteeringValue;

    // Right hand: aim + trigger shoots
    const aimDir: Vec3 = { x: 0, y: 0, z: -1 };
    if (right) {
      const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(
        right.controller.getWorldQuaternion(new THREE.Quaternion())
      );
      aimDir.x = dir.x;
      aimDir.y = dir.y;
      aimDir.z = dir.z;
    }

    return {
      lateral,
      boost: left?.trigger ?? false,   // left trigger = boost
      brake: left?.squeeze ?? false,   // left grip = brake
      aimDir,
      shoot: right?.trigger ?? false,  // right trigger = fire
    };
  }

  private getVRPassengerInput(): PassengerInput {
    const getAim = (hand?: { controller: THREE.XRTargetRaySpace }): Vec3 => {
      if (!hand) return { x: 0, y: 0, z: -1 };
      const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(
        hand.controller.getWorldQuaternion(new THREE.Quaternion())
      );
      return { x: dir.x, y: dir.y, z: dir.z };
    };

    return {
      aimDirL: getAim(this.xrHands.left),
      shootL: this.xrHands.left?.trigger ?? false,
      aimDirR: getAim(this.xrHands.right),
      shootR: this.xrHands.right?.trigger ?? false,
      reload: false,
      special: false,
    };
  }

  dispose(): void {
    this.keys.clear();
  }
}
